import { StageLayer } from '@nolimitcity/slot-launcher/bin/StageLayer';
import { Beheaded } from '../../Beheaded';
import { gsap, Linear, Power2, TimelineLite, Back } from 'gsap';
import { Helper } from '../../../core/utils/Helper';
import { BGType } from './Background';
import { BitmapCountUp } from '../../../core/component/BitmapCountUp';
import { BitmapTextStyleOptions } from '../../../core/setting/text/IText';
import { GameAssets } from '../../GameAssets';
import { AnimationEffects } from '../../../core/effects/AnimationEffects';
import { SpinePlayer } from '../../../core/displayobject/SpinePlayer';
import { MathHelper } from '../../../core/utils/MathHelper';
import { GameData } from '../../server/data/GameData';
import { Sequence } from '../../../core/displayobject/Sequence';
import { BLEND_MODES } from 'pixi.js';
import { SlotGame } from '@nolimitcity/slot-game/bin/SlotGame';
import { SoundConfig } from '../../config/sound/SoundConfig';
import { ArrayHelper } from '../../../core/utils/ArrayHelper';
import { SplashViewSFSPickAndClick } from '../splashScreen/views/SplashViewSFSPickAndClick';
import { GameSetting } from '../../../core/setting/GameSetting';
import { GameConfig } from '../../config/base/GameConfig';
import { BeheadedSound } from '../../sound/BeheadedSound';

export class BeheadedCharacterBackground extends PIXI.Container implements StageLayer {
    private _backgroundCharacter: SpinePlayer;
    private _backgroundCharacterLevelUp: SpinePlayer;

    private _option1: SpinePlayer;
    private _option2: SpinePlayer;
    private _resText: BitmapCountUp;
    private _timeline: gsap.core.Timeline;
    private _selectedIdx: number = 0;

    private txtSpawns: PIXI.Container;
    private splashSpawns: PIXI.Container;
    private _backgroundCouldron: SpinePlayer;

    private _smokeLevelUp: SpinePlayer;

    public isLevelSoundActive: boolean = false;
    private currentLevelSoundPlaying: number = 0;

    public _gameData: GameData;

    private _container: PIXI.Container;

    private featureBuyContainerTl: gsap.core.Timeline;

    private featureBuySymbols: SpinePlayer[];
    private skipFeatureBuyAnimation: boolean = false;

    private couldronFX: Sequence;

    private winStyle = {
        dropShadow: true,
        dropShadowAngle: 7.5,
        fill: 'white',
        fontFamily: 'Helvetica',
        fontSize: 26,
        fontStyle: 'italic',
        fontWeight: 'bold',
        align: 'center',
        miterLimit: 0,
        strokeThickness: 3,
    };

    private splashBall: Sequence;
    private splashBallFX: Sequence;

    private lightningBox: PIXI.Container;

    private readonly _resTextStyle: BitmapTextStyleOptions = {
        fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP_LOW,
        fontSize: 68 / 2,
        letterSpacing: 12,
    };

    private get posValues(): number[] {
        return [5, 10, 25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000];
    }

    constructor() {
        super();
        this.init();
    }

    protected init(): void {
        this._backgroundCharacter = this.addChild(new SpinePlayer('character'));
        this._backgroundCharacter.name = 'Character';
        this._backgroundCharacter.position.set(360, 360);
        this._backgroundCharacter.play({ animation: 'idle_loop', loop: true });
        this._backgroundCharacter.alpha = 0;

        this._backgroundCharacterLevelUp = this.addChild(new SpinePlayer('spine/lvlup_guy.json'));
        this._backgroundCharacterLevelUp.name = 'Character on Level Up';
        this._backgroundCharacterLevelUp.position.set(360, 360);
        this._backgroundCharacterLevelUp.alpha = 0;

        this._backgroundCouldron = this.addChild(new SpinePlayer(BGType.COULDRON_BACK));
        this._backgroundCouldron.name = 'COULDRON';
        this._backgroundCouldron.play({ animation: 'lvl_0_loop', loop: true });
        this._backgroundCouldron.position.set(360, 1150);

        this.splashSpawns = this.addChild(new PIXI.Container());
        this.splashSpawns.position.set(240, 380);
        for (let k = 0; k < 5; k++) {
            const seq = new Sequence({ texturePath: 'blueCouldronSplash' });
            seq.tint = 0xff0000;
            this.splashSpawns.addChild(seq);
        }

        this.splashBall = this.addChild(new Sequence({ texturePath: 'blueSplashBall' }));
        this.splashBall.tint = 0xff0000;
        this.splashBall.anchor.set(0.5);
        this.splashBall.position.set(360, 700);
        this.splashBall.alpha = 0;

        this.splashBallFX = this.addChild(new Sequence({ texturePath: 'blueSplashBall' }));
        this.splashBallFX.tint = 0xff0000;
        this.splashBallFX.anchor.set(0.5);
        this.splashBallFX.position.set(360, 700);
        this.splashBallFX.alpha = 0;
        this.splashBallFX.blendMode = BLEND_MODES.ADD;

        this.lightningBox = this.addChild(new PIXI.Container());
        this.lightningBox.position.set(360, 300);
        this.lightningBox.alpha = 0;

        for (let k = 0; k < 3; k++) {
            const seq = new Sequence({ texturePath: 'lightning' + (k + 1) });
            seq.blendMode = BLEND_MODES.ADD;
            seq.anchor.set(0.5);

            this.lightningBox.addChild(seq);
        }

        this._smokeLevelUp = this.addChild(new SpinePlayer('spine/couldron_middle.json'));
        this._smokeLevelUp.name = 'smokeLevelUp';
        this._smokeLevelUp.position.set(360, 330);

        this._option1 = new SpinePlayer('option1');
        this._option1.interactive = false;
        this._option1.buttonMode = false;
        this._option1.position.set(360, 360);
        this._option1.play({ animation: 'idle_loop', loop: true });
        this._option1.alpha = 0;
        this.addChild(this._option1);

        this._option2 = new SpinePlayer('option2');
        this._option2.interactive = false;
        this._option2.buttonMode = false;
        this._option2.position.set(360, 360);
        this._option2.alpha = 0;
        this._option2.play({ animation: 'idle_loop', loop: true });
        this.addChild(this._option2);

        this._resText = this.addChild(new BitmapCountUp(this._resTextStyle));
        this._resText.anchor = new PIXI.Point(0.5, 0.5);
        this._resText.position.set(360, 700);
        this._resText.alpha = 0;

        // Add some
        this.couldronFX = this.addChild(new Sequence({ texturePath: 'cauld_fire' }));
        this.couldronFX.setClips([{ name: 'tt', from: 0, to: 11 }]);
        this.couldronFX.play({ repeat: -1, speedFactor: 2, clip: 'tt' });
        this.couldronFX.blendMode = BLEND_MODES.ADD;
        this.couldronFX.anchor.set(0.5, 1);
        this.couldronFX.scale.set(4, 2);
        this.couldronFX.position.set(360, 630);
        this.couldronFX.tint = 0x990000;
        this.couldronFX.alpha = 0;

        this.initFeatureSymbols();
        this.txtSpawns = this.addChild(new PIXI.Container());
        this.txtSpawns.position.set(360, 620);
        for (let k = 0; k < this.posValues.length; k++) {
            const txt = new BitmapCountUp(this._resTextStyle);
            txt.text = this.posValues[k] + 'X';
            txt.anchor = new PIXI.Point(0.5, 0.5);
            this.txtSpawns.addChild(txt);
            txt.scale.set(0.3);
        }
    }

    initFeatureSymbols(): void {
        this._container = this.addChild(new PIXI.Container());
        this._container.position.set(360, 800);
        this._container.alpha = 0;
        this.featureBuySymbols = [];

        for (let i = 4; i >= 0; i--) {
            const spine = new SpinePlayer('M_H' + (i + 1), true);
            spine.alpha = 0;
            this.featureBuySymbols.push(spine);
        }
        this.featureBuySymbols.forEach((sym) => {
            this._container.addChild(sym);
        });
    }

    setGameData(data: GameData) {
        this._gameData = data;
    }

    public resetFeatureBuySymbol() {
        this.featureBuyContainerTl?.kill();
        this.featureBuyContainerTl?.clear(true);
        this.skipFeatureBuyAnimation = false;
    }

    public rotateFeatureBuySymbol() {
        this.featureBuyContainerTl = Helper.setTimeline(this.featureBuyContainerTl);
        this.featureBuyContainerTl.to(
            this._container,
            {
                duration: 1.5,
                rotation: 6.28,
                ease: 'Linear.easeInOut',
            },
            0
        );
        for (let i = 0; i < this.featureBuySymbols.length; i++) {
            this.featureBuyContainerTl.to(
                this.featureBuySymbols[i],
                {
                    duration: 1.5,
                    rotation: -6.28,
                    ease: 'Linear.easeInOut',
                },
                0
            );
        }
        this.featureBuyContainerTl.add(() => {
            this._container.rotation = 0;
            for (let i = 0; i < this.featureBuySymbols.length; i++) {
                this.featureBuySymbols[i].rotation = 0;
            }
            if (!this.skipFeatureBuyAnimation) {
                this.rotateFeatureBuySymbol();
            }
        });

        return this.featureBuyContainerTl;
    }

    public animateBackgroundCharacterForFeatureBuy(show: boolean = true, durr: number = 0.5): gsap.core.Timeline {
        let tl = gsap.timeline();
        this.skipFeatureBuyAnimation = false;

        const fromAlpha = show ? 0 : 1;
        const toAlpha = show ? 1 : 0;

        if (show) {
            tl.add(() => {
                if (GameSetting.betType === 'SUPER_FREESPIN') {
                    Beheaded.soundPlayer.playSound(SoundConfig.LVL_START_5_BUY);
                }
                this._backgroundCharacterLevelUp.play({ animation: 'circle', loop: true });
            }, 0);
        }

        tl.fromTo(
            [this._backgroundCharacterLevelUp, this._container],
            { alpha: fromAlpha },
            { alpha: toAlpha, duration: durr },
            0
        );

        tl.fromTo(Beheaded.reelEngineManager, { alpha: toAlpha }, { alpha: fromAlpha, duration: durr }, 0);

        if (!show) {
            tl.add(() => {
                this._backgroundCharacterLevelUp.stop(true);
            });
        }
        tl.to(this, { duration: 0.25 });
        return tl;
    }

    shootUpSymbols(level: number): gsap.core.Timeline {
        const tl = gsap.timeline();
        this.addChild(this._container);

        this.featureBuySymbols.forEach((sp) => (sp.alpha = 0));

        const endPos = [];
        if (level == 3) {
            endPos.push(new PIXI.Point(-200, -380));
            endPos.push(new PIXI.Point(200, -380));
            endPos.push(new PIXI.Point(0, -480));
        } else if (level == 4) {
            endPos.push(new PIXI.Point(-200, -380));
            endPos.push(new PIXI.Point(-100, -430));
            endPos.push(new PIXI.Point(100, -430));
            endPos.push(new PIXI.Point(200, -380));
        } else if (level == 5) {
            endPos.push(new PIXI.Point(-200, -380));
            endPos.push(new PIXI.Point(-100, -430));
            endPos.push(new PIXI.Point(100, -430));
            endPos.push(new PIXI.Point(200, -380));
            endPos.push(new PIXI.Point(0, -480));
        }

        const travelDurr: number = 0.8;
        for (let i = 0; i < level; i++) {
            const target = this.featureBuySymbols[i];
            const delay: number = travelDurr / 3;
            const startAnim: number = i * delay;
            const triggerAnim: number = startAnim + travelDurr;

            target.reset();
            target.visible = true;
            target.x = 0;
            target.alpha = 1;

            tl.to(target, { y: endPos[i].y, duration: travelDurr }, startAnim);

            tl.fromTo(target.scale, { x: 0.4, y: 0.4 }, { x: 1, y: 1, duration: travelDurr }, startAnim);

            tl.add(() => {
                target.play({
                    animation: 'charged',
                    showOnEnd: true,
                    onComplete: () => {
                        target.play({ animation: 'charged_loop', loop: true });
                    },
                });
            }, triggerAnim);

            tl.to(target.scale, { x: 1.25, y: 1.25, duration: target.fetchDuration('charged') / 4 }, triggerAnim);
            tl.to(
                target.scale,
                { x: 1, y: 1, duration: target.fetchDuration('charged') / 4 },
                triggerAnim + target.fetchDuration('charged') / 4
            );

            tl.to(target, { x: endPos[i].x, ease: 'Back.easeOut', duration: travelDurr }, startAnim);
        }

        tl.add(() => {
            this.showSmokeLevelUp(level, undefined, undefined, tl.duration());
        }, 0);
        return tl;
    }

    jumpToIndicators(level: number): gsap.core.Timeline {
        const tl = gsap.timeline({
            onComplete: () => {
                Beheaded.overlayBG.upgradeBGOverlay(level);
                Beheaded.background.upgradeBG(level);
                Beheaded.collectorBar.resetAnimationLevel(level);
            },
        });

        Beheaded.overlayAnim.addChild(this._container);

        const travelDurr: number = 0.6;
        let totalFSGathered: number = 0;
        for (let i = 0; i < level; i++) {
            const target = this.featureBuySymbols[i];
            const delay: number = (i * travelDurr) / 2;

            let toIndicatorPos = Helper.copyLocalPosition(target, Beheaded.collectorBar.indicatorTarget2(i));
            toIndicatorPos = MathHelper.pointSub(toIndicatorPos, target.parent.position);

            tl.to(target, { x: toIndicatorPos.x, y: toIndicatorPos.y, ease: 'Back.easeIn', duration: travelDurr }, delay);
            tl.to(target.scale, { x: 0.35, y: 0.35, ease: 'Back.easeIn', duration: travelDurr }, delay);

            tl.add(() => {
                totalFSGathered += 2;
                SlotGame.keypadPlugin.setZeroBetSpinCounter(totalFSGathered);
                Beheaded.collectorBar.addSpinsView(i + 1, travelDurr / 2, false);
                Beheaded.souldWildController.onLevelUpIndicator();
                Beheaded.collectorBar.activeOnlyIndicator(i + 1);
            }, travelDurr + delay);
            tl.to(target, { alpha: 0 }, travelDurr + delay);
        }

        return tl;
    }

    public showSmokeLevelUp(
        level: number,
        playSound: boolean = true,
        showSmokeOnly: boolean = false,
        durr?: number
    ): gsap.core.Timeline {
        return this._smokeLevelUp.play({
            duration: durr,
            animation: 'Lvl_' + MathHelper.clamp(level, 1, 5),
            onStart: () => {
                if (playSound && !showSmokeOnly) this.playLevelUpSounds(level, true, false);
            },
        });
    }

    public playLevelUpSounds(
        level: number,
        isLevelUp: boolean = false,
        isBonus: boolean = false,
        isPickEnd: boolean = false,
        levelBeforeReset: number = 0
    ): gsap.core.Timeline {
        const tl = gsap.timeline();

        tl.add(() => {
            if (isLevelUp || level > 0) {
                if (Beheaded.model.gameData.wasFeatureBuy && level === 5) {
                    // Beheaded.soundPlayer.playSound(`lvlStart5Buy`);
                } else {
                    Beheaded.soundPlayer.playSound(`lvlStart${level}`);
                }
            }

            if (!this.isLevelSoundActive && level > 0 && level < 5) {
                this.isLevelSoundActive = true;
                this.currentLevelSoundPlaying = level;

                for (let i = 1; i <= 4; i++) {
                    Beheaded.soundPlayer.playSound(`lvl${i}`, 0);
                }
                Beheaded.soundPlayer.fadeSound(`lvl${this.currentLevelSoundPlaying}`, 0, 1, 100);
            } else {
                if (isLevelUp && level !== 5) {
                    Beheaded.soundPlayer.fadeSound(`lvl${this.currentLevelSoundPlaying}`, 1, 0, 500);
                    this.currentLevelSoundPlaying = level;

                    Beheaded.soundPlayer.fadeSound(`lvl${this.currentLevelSoundPlaying}`, 0, 1, 100);
                }

                if (level === 0) {
                    this.isLevelSoundActive = false;

                    if (levelBeforeReset > 2 && !isPickEnd) {
                        if (!Beheaded.model.isWinBelowStakeAllNoRestriction) {
                            Beheaded.soundPlayer.playSound(SoundConfig.FS_OUTRO_END_GOOD);
                        } else {
                            Beheaded.soundPlayer.playSound(SoundConfig.FS_OUTRO_END_BAD);
                        }
                    } else {
                        if (!isBonus && !isPickEnd) {
                            Beheaded.soundPlayer.playSound(SoundConfig.LVL_END);
                        }
                    }

                    Beheaded.soundPlayer.fadeSound(`lvl${this.currentLevelSoundPlaying}`, 1, 0, 500);
                    this.currentLevelSoundPlaying = 0;
                }
            }
        }, 0);
        tl.add(() => {
            if (level === 0) {
                for (let i = 1; i <= 5; i++) {
                    Beheaded.soundPlayer.stopSound(`lvl${i}`);
                }
            }
        }, 0.5);

        return tl;
    }

    public restorePlayLevelUpSounds(level: number): void {
        if (level > 0) {
            this.isLevelSoundActive = true;
            for (let i = 1; i <= 4; i++) {
                Beheaded.soundPlayer.playSound(`lvl${i}`, 0);
            }
            Beheaded.soundPlayer.fadeSound(`lvl${level}`, 0, 1, 100);
        }
    }

    public fadeLevelSoundOnBigWin(level: number, fadeSound: boolean): void {
        if (this.isLevelSoundActive) {
            fadeSound && Beheaded.soundPlayer.fadeSound(`lvl${level}`, 1, 0, 200);
            !fadeSound && Beheaded.soundPlayer.fadeSound(`lvl${level}`, 0, 1, 200);
        }
    }

    public showCharacterLevelUp(level: number): gsap.core.Timeline {
        return this._backgroundCharacterLevelUp.play({
            animation: 'lvl_' + level,
            showOnEnd: true,
            onStart: () => {
                this._backgroundCharacterLevelUp.alpha = 1;
            },
        });
    }

    public showSFSPick(durr: number = 0.35): gsap.core.Timeline {
        let tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.to([this._backgroundCharacter, this._option1, this._option2], {
            alpha: 1,
            duration: durr,
        });

        this.playBottleOut(1);
        this.playBottleOut(2);

        return tl;
    }

    public animateBackgroundCharacter(reverse: boolean = false, durr: number = 0.35): gsap.core.Timeline {
        let tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (reverse) {
            tl.to(this._backgroundCharacterLevelUp, durr, {
                alpha: 0,
            });
        }
        tl.to([this._backgroundCharacter, this._option1, this._option2, this._resText], durr, {
            alpha: reverse ? 0 : 1,
        });
        return tl;
    }

    public applyEvent(cb: any) {
        this._selectedIdx = 0;

        this._option1.interactive = true;
        this._option1.buttonMode = true;
        this._option1.on('mouseover', () => {
            if (!this._selectedIdx) cb.hover(1);
        });

        this._option1.on('mouseout', () => {
            if (!this._selectedIdx) cb.unHover();
        });

        this._option1.on('mousedown', () => {
            if (this._selectedIdx != 1) {
                cb.hover(1, 'click');
                this.playCharHover(1);
                this.playBottleHover(1);

                if (this._selectedIdx === 2) this.playBottleOut(2);

                this._selectedIdx = 1;
            }
        });

        this._option1.on('tap', () => {
            if (this._selectedIdx != 1) {
                cb.hover(1, 'click');
                this.playCharHover(1);
                this.playBottleHover(1);

                if (this._selectedIdx === 2) this.playBottleOut(2);

                this._selectedIdx = 1;
            }
        });

        this._option2.interactive = true;
        this._option2.buttonMode = true;

        this._option2.on('mouseover', () => {
            if (!this._selectedIdx) cb.hover(2);
        });

        this._option2.on('mouseout', () => {
            if (!this._selectedIdx) cb.unHover();
        });

        this._option2.on('mousedown', () => {
            if (this._selectedIdx != 2) {
                cb.hover(2, 'click');
                this.playCharHover(2);
                this.playBottleHover(2);

                if (this._selectedIdx === 1) this.playBottleOut(1);

                this._selectedIdx = 2;
            }
        });

        this._option2.on('tap', () => {
            if (this._selectedIdx != 2) {
                cb.hover(2, 'click');
                this.playCharHover(2);
                this.playBottleHover(2);

                if (this._selectedIdx === 1) this.playBottleOut(1);

                this._selectedIdx = 2;
            }
        });
    }

    public selectOptionForRestore(restoreVal: number) {
        this._selectedIdx = restoreVal;
        SplashViewSFSPickAndClick.selected = restoreVal;
        // return this.selectOptionAnimation();
    }

    playBottleHover(index: number): void {
        const targetObj = [this._option1, this._option2][index - 1];
        const targetDir = ['left', 'right'][index - 1];

        targetObj.play({
            animation: 'hover_' + targetDir + '_start',
            showOnEnd: true,
            onComplete: () => {
                targetObj.play({ animation: 'hover_' + targetDir + '_idle', loop: true });
            },
        });
    }

    public playCharHover(index: number): void {
        const targetDir = ['left', 'right'][index - 1];
        this._timeline = Helper.setTimeline(this._timeline, new gsap.core.Timeline(), true);
        this._timeline.add(this._backgroundCharacter.play({ animation: 'hover_' + targetDir + '_start', showOnEnd: true }));
        this._timeline.add(() => {
            this._backgroundCharacter.play({ animation: 'hover_' + targetDir + '_idle', showOnEnd: true });
        });
    }

    playBottleOut(index: number): void {
        const targetObj = [this._option1, this._option2][index - 1];
        const targetDir = ['left', 'right'][index - 1];

        targetObj.play({
            animation: 'hover_' + targetDir + '_end',
            showOnEnd: true,
            onComplete: () => {
                targetObj.play({ animation: 'idle_loop', loop: true });
            },
        });
    }

    public playCharOut(index: number): void {
        const targetDir = ['left', 'right'][index - 1];

        this._timeline = Helper.setTimeline(this._timeline, new gsap.core.Timeline());
        this._timeline.add(this._backgroundCharacter.play({ animation: 'hover_' + targetDir + '_end', showOnEnd: true }));
        this._timeline.add(() => {
            this._backgroundCharacter.play({ animation: 'idle_loop', showOnEnd: true });
        });
    }

    public clickOptionAnimation() {
        this.selectOptionAnimation();
    }

    public selectOptionAnimation() {
        const targetObj = [this._option1, this._option2][this._selectedIdx - 1];
        const targetOtherObj = [this._option2, this._option1][this._selectedIdx - 1];
        const targetDir = ['left', 'right'][this._selectedIdx - 1];

        const bottleChoosed = ['blue_jar', 'red_jar'][this._selectedIdx - 1];

        if (targetDir === 'left') {
            Beheaded.soundPlayer.playSound(SoundConfig.FS_OUTRO_SELECT_BUTTERFLY);
        } else {
            Beheaded.soundPlayer.playSound(SoundConfig.FS_OUTRO_SELECT_SKULL);
        }

        let tl: gsap.core.Timeline = gsap.timeline({ onUpdate: () => {} });
        tl.add(targetObj.play({ animation: targetDir + '_pick' }), 0);

        tl.add(targetOtherObj.play({ animation: targetDir + '_pick' }), 0);

        tl.to(targetOtherObj, { alpha: 0, duration: tl.duration() / 4 }, 0);

        tl.add(() => {
            this._smokeLevelUp.play({ animation: bottleChoosed, duration: 2 });
        }, tl.duration() / 3);

        tl.add(
            this._backgroundCharacter.play({
                animation: targetDir + '_pick',
                showOnEnd: true,
                onComplete: () => {
                    this._backgroundCharacter.play({
                        animation: 'hover_' + targetDir + '_end',
                        showOnEnd: true,
                        onComplete: () => {
                            this._backgroundCharacter.play({ animation: 'idle_loop', loop: true });
                        },
                    });
                },
            }),
            0
        );

        return tl;
    }

    public resetEvent(isClick: boolean = false) {
        this._option1.off('mouseout');
        this._option1.off('mouseover');
        this._option2.off('mouseout');
        this._option2.off('mouseover');
        if (!isClick) {
            this._option1.off('mousedown');
            this._option2.off('mousedown');
            this._option1.off('tap');
            this._option2.off('tap');
            this._option1.interactive = false;
            this._option1.buttonMode = false;

            this._option2.interactive = false;
            this._option2.buttonMode = false;
        }
    }

    public upgradeBG(level: number) {
        let startAnimName: string = 'lvl_' + level + '_start';
        let loopAnimName: string = 'lvl_' + level + '_loop';
        this._backgroundCouldron.play({
            animation: startAnimName,
            showOnEnd: true,
            onComplete: () => {
                this._backgroundCouldron.play({ animation: loopAnimName, loop: true });
            },
        });
    }

    public reset() {
        this._selectedIdx = 0;
        this._resText.alpha = 0;
        this._resText.y = 700;
        this.resetEvent();
    }

    public showTeaseSkull() {
        const seqs = this.splashSpawns.children as Sequence[];

        const tl = Helper.setTimeline();
        const www = 500;
        const piecePos = www / seqs.length;
        const randPos = ArrayHelper.shuffle(ArrayHelper.initArrayWithValues(seqs.length, (i) => piecePos * i));
        const delayPerText = 0.15;

        for (let k = 0; k < seqs.length; k++) {
            seqs[k].alpha = 1;
            const delay = k * delayPerText;
            seqs[k].x = randPos[k] - www / 2;
            seqs[k].y = (40 * k) % 2;
            tl.add(() => {
                seqs[k].play({ repeat: -1 });
            }, delay);
        }

        tl.to(this, { duration: 4 }, 0);
        return tl;
    }

    private get bothSplash(): Sequence[] {
        return [this.splashBall, this.splashBallFX];
    }

    private get bothSplashScale(): PIXI.Point[] {
        return [this.splashBall.scale, this.splashBallFX.scale];
    }

    stopAnimsCap() {
        (this.lightningBox.children as Sequence[]).forEach((element) => {
            element.stop();
        });
        this.lightningBox.alpha = 0;
        (this.bothSplash as Sequence[]).forEach((element) => {
            element.stop();
            element.alpha = 0;
        });
    }

    public showSkullRes() {
        const isCap: boolean = this._gameData.Win.brokeTheBank;

        let tl: gsap.core.Timeline = new gsap.core.Timeline({
            onComplete: () => {
                (this.splashSpawns.children as Sequence[]).forEach((element) => element.stop());
            },
        });

        this.bothSplash.forEach((v) => {
            this.splashBall.setTexture('blueSplashBall');
            this.splashBall.play({ repeat: -1 });
            v.alpha = 1;
        });
        (this.lightningBox.children as Sequence[]).forEach((seq) => {
            seq.play({
                repeat: -1,
                onRepeatCallback: () => {
                    seq.scale.set(Math.random() > 0.5 ? 1 : -1, Math.random() > 0.5 ? 1 : -1);
                    seq.rotation = Math.random() * (Math.PI * 2);
                },
            });
        });

        tl.fromTo(this.bothSplash, { x: 360, y: 700 }, { y: 300, duration: 1, ease: 'Back.easeIn' }, 0);
        tl.fromTo(this.bothSplashScale, { x: 0.4, y: 0.4 }, { y: 0.6, x: 0.6, duration: 1 }, 0);

        tl.fromTo(this.lightningBox, { alpha: 0 }, { alpha: 1, duration: 0.2 }, 1);
        tl.to([this.bothSplashScale], { y: 1.5, x: 1.5, duration: 0.4, ease: 'Back.easeOut' }, '+=0.1');
        tl.to(this.bothSplashScale, { y: 2.5, x: 2.5, duration: 0.4, ease: 'Back.easeOut' }, '+=0.1');

        tl.fromTo(this.lightningBox.scale, { x: 0.2, y: 0.2 }, { x: 0.4, y: 0.4, duration: 0.4, ease: 'Back.easeOut' }, 1.4);
        tl.to(this.lightningBox.scale, { x: 0.6, y: 0.6, duration: 0.4, ease: 'Back.easeOut' }, 1.8);

        tl.add([
            AnimationEffects.Shake({
                x: 2,
                y: 2,
                obj: this.splashBall,
                hertz: 100,
                duration: 3,
                updateOnComplete: true,
                positionCb: () => new PIXI.Point(360, 300),
                Intensity: 2.75,
                ease: Back.easeIn,
            }),
            AnimationEffects.Shake({
                x: 2,
                y: 2,
                obj: this.splashBallFX,
                hertz: 100,
                duration: 3,
                updateOnComplete: true,
                positionCb: () => new PIXI.Point(360, 300),
                Intensity: 2.75,
                ease: Back.easeIn,
            }),
            AnimationEffects.Shake({
                x: 2,
                y: 2,
                obj: this.lightningBox,
                hertz: 100,
                duration: 3,
                updateOnComplete: true,
                positionCb: () => new PIXI.Point(360, 300),
                Intensity: 2.75,
                ease: Back.easeIn,
            }),
            gsap.to(this.lightningBox.scale, { x: 0.5, y: 0.5, duration: 3, ease: 'Back.easeIn' }),
            gsap.to(this.bothSplashScale, { y: 2, x: 2, duration: 3, ease: 'Back.easeIn' }),
        ]);

        if (!isCap) {
            tl.add(() => {
                this.bothSplash.forEach((v) => {
                    v.stop();
                    v.setTexture('blueMultiplier_blast');
                    v.play({ duration: 0.8 });
                });
            });
            tl.to(this.lightningBox, { alpha: 0, duration: 1.5 });
        }

        const totDurrDiv = tl.duration();
        (this.splashSpawns.children as Sequence[]).forEach((element) => {
            tl.to(element, { alpha: 0, duration: 1 }, totDurrDiv - 1);
        });

        return tl;
    }

    public showTeaseButt() {
        const temp = ArrayHelper.shuffle(this.txtSpawns.children);

        const tl = Helper.setTimeline();
        const www = 500;
        const piecePos = www / temp.length;
        const randPos = ArrayHelper.shuffle(ArrayHelper.initArrayWithValues(temp.length, (i) => piecePos * i));
        const delayPerText = 0.15;
        const travelDur = 1.25;

        for (let k = 0; k < temp.length; k++) {
            const delay = k * delayPerText;
            temp[k].x = randPos[k] - www / 2;

            tl.fromTo(temp[k], { alpha: 0 }, { alpha: 1, duration: 0.2 }, delay);
            tl.fromTo(temp[k], { y: 0 }, { y: -(100 + (k % 2) * 70), duration: travelDur }, delay);

            tl.to(temp[k], { alpha: 0, duration: 0.35 }, delay + travelDur - 0.35);
        }

        return tl;
    }

    public showButtResult(): gsap.core.Timeline {
        let tl: gsap.core.Timeline = new gsap.core.Timeline();

        const valWon = this._gameData.raw.winAmountFirstChoice;
        const totWin = this._gameData.raw.accumulatedRoundWin;
        const valBeforeWin = totWin - valWon;

        let text = this._gameData.Win.winRatio + 'X';
        this._resText.text = text;
        this._smokeLevelUp.play({ animation: 'Lvl_1' });
        Beheaded.counter.changeToBigWinFont(false);

        tl.fromTo(this._resText, { alpha: 0, y: 600 }, { y: 380, alpha: 1, duration: 1.75, ease: 'Back.easeOut' }, 0);

        tl.fromTo(this._resText.scale, { x: 0.3, y: 0.3 }, { x: 0.7, y: 0.7, duration: 1.75 }, 0);

        tl.add(() => {
            this._resText.showValue(valWon);
            this._resText.scale.set(0.7);
        });

        tl.to(this._resText.scale, {
            x: 0.85,
            y: 0.85,
            duration: 0.2,
        });

        tl.to(this._resText.scale, {
            x: 0.7,
            y: 0.7,
            duration: 0.2,
        });

        tl.add(() => {
            Beheaded.counter.playTotalWin(valBeforeWin, 0.25);
        }, '+=0.30');

        tl.to(
            this._resText,
            {
                y: 270,
                duration: 0.5,
                ease: 'Back.easeIn',
            },
            '+=0.30'
        );

        tl.add(() => {
            this._resText.alpha = 0;
            const wRatio = Beheaded.model.gameData.Win.winRatio;

            Beheaded.soundPlayer.playSound(SoundConfig.NORMAL_COUNTUP);

            Beheaded.counter.play(valBeforeWin, totWin, 0.5).add(() => {
                Beheaded.counter.bounceHit(3, 0.25);
                Beheaded.soundPlayer.stopSound(SoundConfig.NORMAL_COUNTUP);

                if (wRatio < 50) Beheaded.soundPlayer.playSound(BeheadedSound.COUNT_UP_ENDS[2]);
                else if (wRatio < 500) Beheaded.soundPlayer.playSound(BeheadedSound.COUNT_UP_ENDS[3]);
                else Beheaded.soundPlayer.playSound(BeheadedSound.AS_WIN_BIG);
            });
        });

        return tl;
    }
}
